import CardsGrid from "@/components/sections/CardsGrid";
import ContentAside from "@/components/sections/ContentAside";
import CTA from "@/components/sections/CTA";
import FAQ from "@/components/sections/FAQ";
import Hero from "@/components/sections/Hero";
import LegalBlock from "@/components/sections/LegalBlock";
import NumberedList from "@/components/sections/NumberedList";
import PageBanner from "@/components/sections/PageBanner";
import ProcessSteps from "@/components/sections/ProcessSteps";
import RichText from "@/components/sections/RichText";
import SplitContent from "@/components/sections/SplitContent";
import SplitList from "@/components/sections/SplitList";
import StandardsList from "@/components/sections/StandardsList";
import ValuesList from "@/components/sections/ValuesList";

/**
 * Dispatcher that picks a section component from `section.type`.
 * Every backend section has a `type` field; unknown types render as RichText.
 */
export default function SectionRenderer({ sections, banner, breadcrumbs }) {
  if (!Array.isArray(sections) || sections.length === 0) return null;

  return (
    <>
      {sections.map((section, i) => {
        // Special override: sections used as page banners on inner pages get PageBanner
        if (section.type === "page_banner" || (i === 0 && banner === "page")) {
          return <PageBanner key={i} section={section} breadcrumbs={breadcrumbs} />;
        }
        switch (section.type) {
          case "hero":
            return <Hero key={i} section={section} />;
          case "cards_grid":
            // "values on home" (values-tagged cards on navy w/ no icons) → ValuesList
            if (isValuesGrid(section)) return <ValuesList key={i} section={section} />;
            return <CardsGrid key={i} section={section} />;
          case "process_steps":
            // "Six reasons partner with us" style (many numbered cards) uses NumberedList
            if ((section.cards || []).length > 4) return <NumberedList key={i} section={section} />;
            return <ProcessSteps key={i} section={section} />;
          case "numbered_list":
            return <NumberedList key={i} section={section} />;
          case "standards_list":
            return <StandardsList key={i} section={section} />;
          case "faq":
            return <FAQ key={i} section={section} />;
          case "split_list":
            return <SplitList key={i} section={section} />;
          case "list_items":
            return <SplitList key={i} section={section} />;
          case "content_aside":
            return <ContentAside key={i} section={section} />;
          case "split_content":
            return <SplitContent key={i} section={section} />;
          case "legal_block":
            return <LegalBlock key={i} section={section} first={i === 1} />;
          case "cta":
            return <CTA key={i} section={section} />;
          case "rich_text":
          default:
            return <RichText key={i} section={section} />;
        }
      })}
    </>
  );
}

function isValuesGrid(section) {
  if (!section) return false;
  const cards = section.cards || [];
  if (cards.length < 4) return false;
  // Values cards on the reference: short single-word title + short description, no icon, no href.
  const allShortTitles = cards.every((c) => (c.title || c.description || "").length < 40);
  const noIcons = cards.every((c) => !c.icon);
  const noHrefs = cards.every((c) => !c.href);
  const noNumbers = cards.every((c) => !c.number);
  return allShortTitles && noIcons && noHrefs && noNumbers && (section.theme === "navy" || cards.length >= 6);
}
